// src/components/EditRecordSectionsModal.jsx
//
// Modal for SuperAdmin-only direct editing of a checkout record's section
// statuses. Toggles each section between 'na' and 'pending'.

import React, { useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CHECKOUT_SECTIONS, KA_COLORS } from '../utils/checkoutHelpers';

const SECTION_STATUS_LABELS = {
  na: 'N/A',
  pending: 'Pending',
  signed: 'Signed',
};

const formTypeToAppliesToBucket = (formType) => {
  if (formType === 'ms') return 'ms';
  if (formType === 'senior') return 'senior';
  if (formType === 'international') return 'international';
  return '9-11';
};

export default function EditRecordSectionsModal({ record, onClose, onSaved }) {
  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const formTypeBucket = formTypeToAppliesToBucket(record?.formType);

  const rows = useMemo(() => {
    return CHECKOUT_SECTIONS.map((section) => {
      const data = record?.sections?.[section.id] || {};
      const currentStatus = data.status || 'na';
      const pendingEdit = edits[section.id];
      const displayedStatus = pendingEdit || currentStatus;
      const isSigned = currentStatus === 'signed';
      const isExpectedForForm = Array.isArray(section.appliesTo) &&
        section.appliesTo.includes(formTypeBucket);
      return {
        sectionId: section.id,
        label: humanizeSectionId(section.id),
        currentStatus,
        displayedStatus,
        isSigned,
        isExpectedForForm,
        isFinalSignoff: !!section.isFinalSignoff,
        boardersOnly: !!section.boardersOnly,
        isDirty: pendingEdit && pendingEdit !== currentStatus,
      };
    });
  }, [record, edits, formTypeBucket]);

  const dirtyCount = rows.filter((r) => r.isDirty).length;

  const handleToggle = (sectionId) => {
    setError(null);
    setEdits((prev) => {
      const row = rows.find((r) => r.sectionId === sectionId);
      if (!row || row.isSigned) return prev;
      const next = row.displayedStatus === 'pending' ? 'na' : 'pending';
      const newEdits = { ...prev };
      if (next === row.currentStatus) {
        delete newEdits[sectionId];
      } else {
        newEdits[sectionId] = next;
      }
      return newEdits;
    });
  };

  const handleSave = async () => {
    if (dirtyCount === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, 'us-central1'),
        'editRecordSections',
      );
      const res = await fn({
        recordId: record.id || record.recordId,
        sectionUpdates: edits,
        reason: reason.trim() || null,
      });
      if (res?.data?.ok) {
        onSaved?.({
          completionPercent: res.data.completionPercent,
          recordStatus: res.data.recordStatus,
          applied: res.data.applied,
        });
        onClose();
      } else {
        setError('Unexpected response from server.');
      }
    } catch (err) {
      console.error('editRecordSections failed:', err);
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Edit Sections</div>
            <div style={styles.subtitle}>
              {record?.studentName || record?.studentId} ·{' '}
              {record?.grade || '—'} · {record?.formType || '—'}
            </div>
          </div>
          <button type="button" onClick={onClose} style={styles.closeBtn}>
            ×
          </button>
        </div>

        <div style={styles.warning}>
          <strong>Direct edit:</strong> this changes the record's section map
          without going through the normal signoff flow. Use it only when a
          record is missing or wrongly has a section. Already-signed sections
          can't be edited here — undo them via the section card first.
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Section</th>
                <th style={styles.th}>Current</th>
                <th style={styles.th}>New</th>
                <th style={styles.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.sectionId}
                  style={{
                    ...styles.tr,
                    background: row.isDirty
                      ? '#fff8e1'
                      : row.isSigned
                      ? '#f9f9fa'
                      : 'transparent',
                  }}
                >
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600 }}>{row.label}</div>
                    <div style={styles.sectionMeta}>
                      <code style={styles.codeBadge}>{row.sectionId}</code>
                      {row.isFinalSignoff && (
                        <span style={styles.metaPill}>final</span>
                      )}
                      {row.boardersOnly && (
                        <span style={styles.metaPill}>boarders</span>
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <StatusPill status={row.currentStatus} />
                  </td>
                  <td style={styles.td}>
                    {row.isSigned ? (
                      <span style={styles.disabledText}>
                        (signed — undo first)
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleToggle(row.sectionId)}
                        style={{
                          ...styles.toggleBtn,
                          ...(row.displayedStatus === 'pending'
                            ? styles.toggleBtnPending
                            : styles.toggleBtnNa),
                        }}
                      >
                        {row.displayedStatus === 'pending' ? 'Pending' : 'N/A'}
                      </button>
                    )}
                  </td>
                  <td style={styles.td}>
                    {!row.isSigned && !row.isExpectedForForm && row.displayedStatus === 'pending' && (
                      <span style={styles.warnText}>
                        Catalog says this section doesn't normally apply to
                        a {record?.formType} student — proceeding will override.
                      </span>
                    )}
                    {row.sectionId === 'finance' && row.isDirty && (
                      <span style={styles.warnText}>
                        Finance edits may be overwritten by the next finance
                        roster upload.
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.reasonRow}>
          <label style={styles.reasonLabel}>
            Reason (optional, logged in audit trail):
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Senior promoted late, missing UCO"
            style={styles.reasonInput}
          />
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.footer}>
          <div style={styles.summary}>
            {dirtyCount === 0
              ? 'No changes'
              : `${dirtyCount} section${dirtyCount === 1 ? '' : 's'} will be updated`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelBtn}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || dirtyCount === 0}
              style={{
                ...styles.saveBtn,
                opacity: saving || dirtyCount === 0 ? 0.5 : 1,
                cursor: saving || dirtyCount === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Apply changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const colors = {
    na: { bg: '#f3f4f6', fg: '#6b7280' },
    pending: { bg: '#fff3cd', fg: '#856404' },
    signed: { bg: '#d4edda', fg: '#155724' },
  };
  const c = colors[status] || colors.na;
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        display: 'inline-block',
      }}
    >
      {SECTION_STATUS_LABELS[status] || status}
    </span>
  );
};

function humanizeSectionId(sid) {
  const map = {
    scheduling: 'Scheduling',
    science_books: 'Science — Returned Books',
    math_books: 'Mathematics — Returned Books',
    history_books: 'History — Returned Books',
    world_lang_books: 'World Languages — Returned Books',
    art_books: 'Art Department — Returned Books',
    athletics: 'Athletics',
    library: 'Library',
    uco: 'UCO',
    advancement: 'Alumni Relations / Advancement',
    hr_residency: 'HR & General Admin',
    finance: 'Finance',
    it: 'IT Help Desk',
    dorm: 'Dorm Faculty',
    osl: 'OSL — Final Clearance',
    ms_final: 'Middle School — Final Clearance',
  };
  return map[sid] || sid;
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: 8,
    width: 'min(900px, 95vw)',
    maxHeight: '90vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: KA_COLORS.navy,
  },
  subtitle: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: 28,
    color: '#999',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
    width: 32,
    height: 32,
  },
  warning: {
    margin: '12px 20px 0',
    padding: '10px 12px',
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 4,
    fontSize: 12,
    color: '#5c4a00',
  },
  tableWrap: {
    margin: '12px 20px',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    padding: '8px 12px',
    background: '#f9fafb',
    textAlign: 'left',
    fontWeight: 600,
    color: '#374151',
    fontSize: 12,
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '10px 12px',
    verticalAlign: 'top',
  },
  sectionMeta: {
    marginTop: 3,
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  codeBadge: {
    fontSize: 10,
    color: '#888',
    background: '#f3f4f6',
    padding: '1px 4px',
    borderRadius: 3,
  },
  metaPill: {
    fontSize: 9,
    padding: '1px 5px',
    background: '#e0f2fe',
    color: '#075985',
    borderRadius: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  toggleBtn: {
    border: '1px solid',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    minWidth: 80,
  },
  toggleBtnNa: {
    background: '#f3f4f6',
    color: '#6b7280',
    borderColor: '#d1d5db',
  },
  toggleBtnPending: {
    background: '#fff3cd',
    color: '#856404',
    borderColor: '#ffe082',
  },
  disabledText: {
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
  },
  warnText: {
    fontSize: 11,
    color: '#b45309',
    fontStyle: 'italic',
  },
  reasonRow: {
    margin: '8px 20px',
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  reasonLabel: {
    fontSize: 12,
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  reasonInput: {
    flex: 1,
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 13,
    outline: 'none',
  },
  error: {
    margin: '0 20px 8px',
    padding: 10,
    background: '#FFEBEE',
    color: '#C62828',
    fontSize: 12,
    borderRadius: 4,
  },
  footer: {
    padding: '14px 20px',
    borderTop: '1px solid #e5e7eb',
    background: '#f9fafb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summary: {
    fontSize: 12,
    color: '#666',
  },
  cancelBtn: {
    padding: '8px 16px',
    background: '#fff',
    border: '1px solid #d1d5db',
    color: '#374151',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '8px 18px',
    background: KA_COLORS.navy,
    border: 'none',
    color: '#fff',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
  },
};